import { WorkspaceCreateField } from "@/components/shared/workspace-create-field";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { ExpenseAttachments } from "./expense-attachments";
import {
  EXPENSE_FUNDING_SOURCE_LABELS,
  EXPENSE_PAYMENT_METHOD_LABELS,
} from "./expense-labels";

import type { ExpenseFormValues } from "./expense-schema";
import type { ExistingReceipt } from "./use-expense-form";
import type { Expense } from "@artisancode/api-types";
import type { UseFormReturn } from "react-hook-form";

interface ExpenseDetailsSectionProps {
  form: UseFormReturn<ExpenseFormValues>;
  editing: Expense | null;
  previewSeq: string;
  selectedFiles: File[];
  existingReceipts: ExistingReceipt[];
  isSaving: boolean;
  addFiles: (files: FileList | File[]) => void;
  removeSelectedFile: (index: number) => void;
  removeExistingReceipt: (key: string) => void;
}

export function ExpenseDetailsSection({
  form,
  editing,
  previewSeq,
  selectedFiles,
  existingReceipts,
  isSaving,
  addFiles,
  removeSelectedFile,
  removeExistingReceipt,
}: ExpenseDetailsSectionProps) {
  return (
    <div className="space-y-3.5 md:flex md:min-h-0 md:flex-col md:gap-3.5">
      {/* Metode Pembayaran & Sumber Dana */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FormField
          control={form.control}
          name="paymentMethod"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Metode Pembayaran</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {Object.entries(EXPENSE_PAYMENT_METHOD_LABELS).map(
                    ([v, l]) => (
                      <SelectItem key={v} value={v}>
                        {l}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="fundingSource"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Sumber Dana</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {Object.entries(EXPENSE_FUNDING_SOURCE_LABELS).map(
                    ([v, l]) => (
                      <SelectItem key={v} value={v}>
                        {l}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* No. Pengeluaran & Referensi */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="grid min-w-0 gap-2">
          <Label htmlFor="expense-code">No. Pengeluaran</Label>
          <Input
            id="expense-code"
            disabled
            className="font-mono"
            value={
              editing
                ? (editing.code ?? "-")
                : previewSeq
                  ? `${previewSeq} (otomatis)`
                  : "-"
            }
          />
        </div>
        <FormField
          control={form.control}
          name="referenceNote"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Referensi / Bukti</FormLabel>
              <FormControl>
                <Input
                  placeholder="No. struk / invoice (Opsional)"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Catatan / Keterangan (Opsional) */}
      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Catatan Tambahan (Opsional)</FormLabel>
            <FormControl>
              <Input placeholder="Contoh: Belanja Bulanan Cabang" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* File Attachment & Upload Zone */}
      <ExpenseAttachments
        selectedFiles={selectedFiles}
        existingReceipts={existingReceipts}
        isSaving={isSaving}
        onAddFiles={addFiles}
        onRemoveSelectedFile={removeSelectedFile}
        onRemoveExistingReceipt={removeExistingReceipt}
      />

      <WorkspaceCreateField
        control={form.control}
        name="workspaceId"
        editing={!!editing}
      />
    </div>
  );
}
